import { Component } from "@angular/core";
import * as cronjsMatcher from '@datasert/cronjs-matcher';
import moment from 'moment';
import { CronBuilderDialog } from "../../../../components/common/cronbuilder/cron.builder.dialog";
import { MessageBoxHelper } from "../../../../components/dialogs/messagebox/messagebox.dialog";
import { TranslateFormatText } from "../../../../helpers/array.helpers";
import { MessageBoxButtons } from "../../../../models/enums/messageboxbuttons.enum";
import { MessageBoxIcon } from "../../../../models/enums/messageboxicon.enum";
import { BaseTriggerControl } from "./base.trigger.control";

@Component({
    selector: 'timetrigger-control',
    templateUrl: './time.trigger.control.html',
    styleUrls: ['./time.trigger.control.css']
})
export class TimeTriggerControl extends BaseTriggerControl {

    DataValue: any;

    // IANA names as the browser knows them; the server resolves the same names. Empty means UTC,
    // which is how every trigger without a zone has been running.
    Zeitzonen: { name: string, value: string }[] = [{ name: 'UTC', value: '' }].concat(
        ((Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone') as string[] : ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich'])
            .filter(z => z !== 'UTC')
            .map(z => ({ name: z, value: z })));

    ngOnInit(): void {
        this.OnTriggerSet();
    }

    OnTriggerSet() {
        const dv = {
            FixedTimes: [],
            CronExpressions: []
        };
        if (this.TriggerValue) {
            if (!this.TriggerValue.FixedTimes) {
                this.TriggerValue.FixedTimes = [];
            } else {
                this.TriggerValue.FixedTimes.forEach((ft: any) => {
                    dv.FixedTimes.push({
                        Date: ft
                    });
                });
            }
            if (!this.TriggerValue.CronExpressions) {
                this.TriggerValue.CronExpressions = [];
            } else {
                this.TriggerValue.CronExpressions.forEach((ce: any) => {
                    dv.CronExpressions.push({
                        Expression: ce
                    });
                });
            }
        }
        this.DataValue = dv;
    }

    addFixedTime() {
        const date = {
            Date: new Date()
        };
        if (this.TriggerValue) {
            this.TriggerValue.FixedTimes.push(date.Date);
            this.OnTriggerHasChanged();
        }
        if (this.DataValue) {
            this.DataValue.FixedTimes.push(date);
        }
    }

    onDateChanged(i: number) {
        if (this.TriggerValue && this.DataValue) {
            this.TriggerValue.FixedTimes[i] = this.DataValue.FixedTimes[i].Date;
            this.OnTriggerHasChanged();
        }
    }

    removeFixedTime(i: number) {
        if (this.TriggerValue) {
            this.TriggerValue.FixedTimes.splice(i, 1);
            this.OnTriggerHasChanged();
        }
        if (this.DataValue) {
            this.DataValue.FixedTimes.splice(i, 1);
        }
    }

    addCron() {
        const cron = {
            Expression: '* * * * * ? *'
        };
        if (this.TriggerValue) {
            this.TriggerValue.CronExpressions.push(cron.Expression);
            this.OnTriggerHasChanged();
        }
        if (this.DataValue) {
            this.DataValue.CronExpressions.push(cron);
        }
    }

    onCronChanged(i: number) {
        if (this.TriggerValue && this.DataValue) {
            this.TriggerValue.CronExpressions[i] = this.DataValue.CronExpressions[i].Expression;
            this.OnTriggerHasChanged();
        }
    }

    removeCron(i: number) {
        if (this.TriggerValue) {
            this.TriggerValue.CronExpressions.splice(i, 1);
            this.OnTriggerHasChanged();
        }
        if (this.DataValue) {
            this.DataValue.CronExpressions.splice(i, 1);
        }
    }

    showCronEditor(i: number) {
        if (this.DataValue) {
            const ce = this.DataValue.CronExpressions[i];
            const that = this;
            CronBuilderDialog.ShowDialog(ce.Expression, (exp: any) => {
                ce.Expression = exp;
                that.onCronChanged(i);
            });
        }
    }

    showNextExecutionMessageBox() {
        if (this.TriggerValue) {
            const tft = new TranslateFormatText('');
            if (this.TriggerValue.IsEnabled) {
                let result: string;
                let actDate = new Date();
                if (this.TriggerValue.CronExpressions && this.TriggerValue.CronExpressions.length > 0) {
                    const ce = this.TriggerValue.CronExpressions.join('|');
                    let startdate: string;
                    if (this.TriggerValue.StartDate instanceof Date) {
                        if (this.TriggerValue.StartDate > actDate) {
                            startdate = this.TriggerValue.StartDate.toISOString();
                        }
                    } else if (typeof this.TriggerValue.StartDate == 'string') {
                        if (new Date(this.TriggerValue.StartDate) > actDate) {
                            startdate = this.TriggerValue.StartDate;
                        }
                    }
                    const options = {
                        matchCount: 1,
                        hasSeconds: true,
                        startAt: startdate,
                        // Same zone as the server plans in (TimeEndPoint.Zeitplaene).
                        timezone: this.TriggerValue.TimeZone || 'Etc/UTC'
                    };
                    const futureMatches = cronjsMatcher.getFutureMatches(ce, options);
                    if (futureMatches.length > 0) {
                        result = futureMatches[0];
                    }
                } else if (this.TriggerValue.FixedTimes && this.TriggerValue.FixedTimes.length > 0) {
                    let startDate = typeof this.TriggerValue.StartDate == 'string' ? new Date(this.TriggerValue.StartDate) : this.TriggerValue.StartDate;
                    if (this.TriggerValue.StartDate instanceof Date && startDate > actDate) {
                        actDate = startDate;
                    }
                    let milSecs = Number.MAX_SAFE_INTEGER;
                    this.TriggerValue.FixedTimes.forEach((ft: any) => {
                        const date = new Date(ft);
                        const secs = date.getTime() - actDate.getTime();
                        if (secs > 0 && secs < milSecs) {
                            milSecs = secs;
                            result = date.toISOString();
                        }
                    });
                }
                if (result) {
                    const mom = moment(result);
                    tft.TranslateText = mom.format("dddd") + ", " + mom.format("LL") + ", " + mom.format("LTS");
                } else {
                    tft.TranslateText = "@@NoNextExecution";
                }
            } else {
                tft.TranslateText = "@@Disabled";
            }
            MessageBoxHelper.ShowDialog(tft, new TranslateFormatText('@@NextRun'), MessageBoxButtons.Ok, MessageBoxIcon.Information);
        }
    }
}